import { responsiveFontSizes, ThemeOptions } from "@mui/material";
import { grey } from '@mui/material/colors';

import * as Dtos from "@/api/dtos";

import { AppSettings } from '@/AppSettings';

import colorLogoNew from '@/assets/img/logo-h-c-new.png';
import whiteLogoNew from '@/assets/img/logo-h-w-new.png';

import { BctApiExtension } from '@ngt/opms-bctapi';
import { EventDefinitionCollectionResponse, FormDefinitionCollectionResponse, GetFormMetadataResponse, GetUserResponse, OnlinePatientManagement, PatientStateCollectionResponse, TreatmentCollectionResponse } from "@ngt/opms";

const createOnlinePatientManagement = (
    user?: GetUserResponse, 
    formMetadata?: GetFormMetadataResponse, 
    eventDefinitions?: EventDefinitionCollectionResponse, 
    formDefinitions?: FormDefinitionCollectionResponse, 
    patientStates?: PatientStateCollectionResponse, 
    treatments?: TreatmentCollectionResponse
) => {
    const publicRoute = [
        AppSettings.consentInformationUrl,
        AppSettings.consentParticipantUrl,
    ].some(url => url && global.location.href.includes(url));

    if (!user && !publicRoute) {
        if (global.location.search) {
            global.location.href = '/auth/openiddict' + global.location.search;
            return;
        } else {
            global.location.href = '/auth/openiddict';
            return; 
        }
    }
    
    if (!formMetadata && !publicRoute) {
        throw Error("Form Metadata could not be retrieved!");
    }
    
    const onlinePatientManagement = new OnlinePatientManagement({
        trialOptions: {
            organisationLogoLight: colorLogoNew,
            organisationLogoDark: whiteLogoNew,
            organisationName: "Breast Cancer Trials",
            trialName: "OPT-PEMBRO" ,
            protocolId: "BCT 2601",
            homePageUrl: AppSettings.trialPortalUrl ?? 'https://trialportal.breastcancertrials.org.au/'
        },
        serviceStackClient: {
            baseUrl: AppSettings.client.baseUrl,
            basePath: AppSettings.client.basePath
        },
        dtos: Dtos,
        themeOptions: {
            overrideChosenTheme: AppSettings.palette.overrideTheme,
            beforeThemeCreated: (themeOptions: any, chosenTheme: any) => {
                let newOptions: ThemeOptions = {
                    ...themeOptions,
                    palette: {
                        ...themeOptions.palette,
                        primary: {
                            main: chosenTheme === 'dark' ? AppSettings.palette.primaryDark : AppSettings.palette.primaryLight
                        },
                        secondary: {
                            main: chosenTheme === 'dark' ? AppSettings.palette.secondaryDark : AppSettings.palette.secondaryLight 
                        },
                        error: {
                            main: chosenTheme === 'dark' ? AppSettings.palette.errorDark : AppSettings.palette.errorLight, 
                            // dark: AppSettings.palette.errorDark,
                        }
                    },
                    typography: (palette) => ({
                        h1: {
                            fontSize: '2rem',
                            color: chosenTheme === 'dark' ? palette.secondary.light : palette.secondary.main,
                        },
                        h2: {
                            fontSize: '1.5rem',
                            color: chosenTheme === 'dark' ? palette.primary.light : palette.primary.main,
                            fontWeight: '400' 
                        },
                        h3: {
                            fontSize: '1.25rem',
                            color: chosenTheme === 'dark' ? palette.secondary.light : palette.secondary.main,
                            fontWeight: '400' 
                        },
                        h4: {
                            fontSize: '1.20rem',
                            color: chosenTheme === 'dark' ? palette.primary.light : palette.primary.main,
                            fontWeight: '400'
                        },
                        h5: {
                            fontSize: '1.15rem',
                            color: chosenTheme === 'dark' ? palette.secondary.light : palette.secondary.main,
                            fontWeight: '400'
                        },
                        h6: {
                            fontSize: '1.10rem',
                            color: chosenTheme === 'dark' ? palette.primary.light : palette.primary.main,
                            fontWeight: '400'
                        }
                    })
                };

                if (chosenTheme !== 'dark') {
                    if (!newOptions.palette) {
                        newOptions.palette = {}
                    }
            
                    newOptions.palette.background = {
                        default: grey[200]
                    }
                }

                return newOptions;
            },
            afterThemeCreated: (theme: any) => {
                return responsiveFontSizes(theme);
            }
        } as any,
        initialFormMetadata: formMetadata,
        initialAuthentication: user,
        initialEventDefinitions: eventDefinitions,
        initialFormDefinitions: formDefinitions,
        initialPatientStates: patientStates,
        initialTreatments: treatments,
        disableAuthentication: !user && publicRoute,
        extensions: [
            new BctApiExtension({})
        ]
    });
    
    onlinePatientManagement.serviceStackClient.exceptionFilter = (res: Response, _error: any) => {
        if (res.status === 401 && !publicRoute) {
            global.location.href = `/auth/openiddict?redirect=${encodeURIComponent(global.location.pathname + global.location.search)}`;
        }
    };

    return onlinePatientManagement;
}

export default createOnlinePatientManagement;
